import { LostStatuses } from "../../../types/LostStatuses";
import { FoundStatuses } from "../../../types/FoundItemTypes";
import { permissions } from "../../../utils/constants/permissions";
import { commonLabels } from "../../../utils/constants/labels";
import { ReactNode } from "react";

type StatusOption = {
  value: number;
  label: ReactNode;
  labelText: string;
  key: string;
  permissionKey: string;
};

export const lostReportStatusOptions: StatusOption[] = [
  { value: LostStatuses.DraftIncomplete, label: commonLabels.draftIncomplete, labelText: commonLabels.draftIncomplete, key: `${LostStatuses.DraftIncomplete}`, permissionKey: permissions.IsDraftIncompleteStatusEnabled },
  { value: LostStatuses.DraftCompleted, label: commonLabels.draftCompleted, labelText: commonLabels.draftCompleted, key: `${LostStatuses.DraftCompleted}`, permissionKey: permissions.IsDraftCompletedStatusEnabled },
  { value: LostStatuses.NotPaid, label: commonLabels.notPaid, labelText: commonLabels.notPaid, key: `${LostStatuses.NotPaid}`, permissionKey: permissions.IsNotPaidStatusEnabled },
  { value: LostStatuses.Open, label: commonLabels.open, labelText: commonLabels.open, key: `${LostStatuses.Open}`, permissionKey: permissions.IsOpenStatusEnabled },
  { value: LostStatuses.Pending, label: commonLabels.pending, labelText: commonLabels.pending, key: `${LostStatuses.Pending}`, permissionKey: permissions.IsPendingStatusEnabled },
  { value: LostStatuses.Review, label: commonLabels.review, labelText: commonLabels.review, key: `${LostStatuses.Review}`, permissionKey: permissions.IsReviewStatusEnabled },
  { value: LostStatuses.InProcess, label: commonLabels.inProcess, labelText: commonLabels.inProcess, key: `${LostStatuses.InProcess}`, permissionKey: permissions.IsInProcessStatusEnabled },
  { value: LostStatuses.LFNotified, label: commonLabels.lFNotified, labelText: commonLabels.lFNotified, key: `${LostStatuses.LFNotified}`, permissionKey: permissions.IsLFNotifiedStatusEnabled },
  { value: LostStatuses.Urgent, label: <span style={{ color: "#A8071A" }}>Urgent</span>, labelText: "Urgent", key: `${LostStatuses.Urgent}`, permissionKey: permissions.IsOpenStatusEnabled },
  { value: LostStatuses.Overdue, label: <span style={{ color: "orange" }}>{commonLabels.overdue}</span>, labelText: commonLabels.overdue, key: `${LostStatuses.Overdue}`, permissionKey: permissions.IsOverdueStatusEnabled },
  { value: LostStatuses.Deleted, label: <span style={{ color: "red" }}>{commonLabels.delete}</span>, labelText: commonLabels.delete, key: `${LostStatuses.Deleted}`, permissionKey: permissions.IsCloseStatusEnabled },
];

// Flow: Open → Review → InProcess → LFNotified (forward) with undo step-backs.
// Review's "hold-off" highlight window expires `recentPaymentHighlightHours` after
// the customer paid; a silent cron then promotes the claim Review → InProcess.
// Manual selector here mirrors that so admins can move things forward early or
// undo if something was promoted prematurely.
const lostStatusTransitionMap: Record<number, number[]> = {
  [LostStatuses.DraftIncomplete]: [LostStatuses.Deleted],
  [LostStatuses.DraftCompleted]: [LostStatuses.Deleted],
  [LostStatuses.NotPaid]: [LostStatuses.Deleted],
  [LostStatuses.Open]: [LostStatuses.Review, LostStatuses.Deleted],
  [LostStatuses.Pending]: [LostStatuses.Review, LostStatuses.Deleted],     // grandfathered → next step is Review
  [LostStatuses.Review]: [LostStatuses.Open, LostStatuses.InProcess, LostStatuses.LFNotified, LostStatuses.Deleted],
  [LostStatuses.InProcess]: [LostStatuses.Review, LostStatuses.LFNotified, LostStatuses.Deleted],
  [LostStatuses.LFNotified]: [LostStatuses.InProcess, LostStatuses.Review, LostStatuses.Deleted],
  [LostStatuses.Urgent]: [LostStatuses.Open, LostStatuses.Review, LostStatuses.Deleted],
  [LostStatuses.Overdue]: [LostStatuses.Open, LostStatuses.Deleted],
  [LostStatuses.Deleted]: [LostStatuses.Open],
};

export const getAllowedLostStatusTargets = (status?: number) => {
  if (typeof status !== "number") {
    return lostStatusTransitionMap[LostStatuses.Open];
  }
  return lostStatusTransitionMap[status] || [];
};

export const LostToFoundStatusMap: Record<number, number | undefined> = {
  [LostStatuses.InProcess]: FoundStatuses.Open,
  [LostStatuses.Deleted]: FoundStatuses.Open,
};

export const lostReportUrgentStatus = { value: LostStatuses.Urgent, label: "Urgent", key: `${LostStatuses.Urgent}` };

export const buildShipFromAddress = (data: any) => {
  const addressParts = [
    data?.address1,
    data?.address2,
    `${data?.city ?? data?.city?.name ?? ""} ${data?.zip ?? ""}`,
    (data?.state ?? data?.state?.name ?? ""),
    (data?.country ?? data?.country?.name ?? "")
  ];

  // Filter out any undefined or empty values
  const filteredParts = addressParts?.filter(part => part);

  // Join the parts with a comma and space
  return filteredParts?.join(', ');
};

export const timeFormat = "HH:mm";
